import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { getNumericExtent } from '../constants/exploreFilterEngine';
import {
  COLUMN_LABEL_BY_KEY,
  NUMERIC_UNIT_BY_KEY,
  displayScale,
  formatNumericForChip
} from '../constants/columnFilterTypes';
import {
  parseAcceptedNumber,
  parserOptsForColumn,
  processNumericInput
} from '../utils/parseNumericInput';

/**
 * Numeric range filter — min / "to" / max inputs with the current-set extent
 * shown as help text. Used for Sales, Employees, EBIT Margin, CAGR 3Y.
 *
 * Draft state lives in the parent (ColumnFilterPopover); the *committed*
 * numeric range value flows in via the `value` prop and out via `onChange`.
 * Empty fields are tolerated — submitting with both blank clears the column
 * filter.
 *
 * The "current set" extent is computed against the full loaded result set
 * (`results` prop), not the currently-filtered subset, so the analyst sees the
 * full range available to pick from regardless of other active filters.
 *
 * V1.3 — display-scale aware. Some fields (e.g. `revenue`) are stored as raw
 * units (raw euros) but displayed to analysts in millions. The popover always
 * shows the *display* value to the user; the engine always stores the *raw*
 * value. Conversion happens at the I/O boundary here:
 *   - committed range and current-set extent are divided by
 *     `displayScale(columnKey)` when seeded into the local input string
 *   - parsed user input is multiplied by `displayScale(columnKey)` before
 *     being passed up via `onChange`.
 * The filter engine stays unit-agnostic — it compares raw values.
 *
 * TD-2229 — locale-agnostic input parsing. The user-typed string lives in
 * local state so transient typing states like `-` (before `-5`) or `8.`
 * (before `8.5`) are not clobbered by React reflows. Parsing rules mirror
 * `src/components/inputs/Input.jsx` exactly (see `utils/parseNumericInput
 * .js`): both `8.5` and `8,5` parse to 8.5 regardless of browser locale,
 * and bad input is silently rejected — the in-flight character simply does
 * not appear in the input box, matching the Profiler criteria search form
 * field behavior so analysts see the same UX in both places.
 */
const FilterNumericRange = observer(({ columnKey, value, onChange, results }) => {
  const scale = displayScale(columnKey);
  const extentRaw = getNumericExtent(results, columnKey);
  const unit = NUMERIC_UNIT_BY_KEY[columnKey] ?? '';
  const label = COLUMN_LABEL_BY_KEY[columnKey] ?? columnKey;
  const range = value?.range ?? {};
  const opts = parserOptsForColumn(columnKey);

  // Convert a raw stored value to the display-unit string the user sees.
  // `toPrecision(12)` drops IEEE float noise like `0.085 / 0.01 ===
  // 8.499999999999999` so a Spotlight-injected raw value renders cleanly.
  const toDisplay = (raw) =>
    raw == null ? '' : String(Number((raw / scale).toPrecision(12)));

  const [localMinStr, setLocalMinStr] = useState(() => toDisplay(range.min));
  const [localMaxStr, setLocalMaxStr] = useState(() => toDisplay(range.max));

  // Re-seed local strings whenever the *numeric* committed value changes.
  // We deliberately key on `range.min` / `range.max` (the actual numbers)
  // rather than the `value` object reference, because every keystroke that
  // commits upward creates a new object reference even when the underlying
  // number didn't change. Keying on the object reference would clobber the
  // user's trailing dot. Keying on the value lets external sources
  // (Spotlight sync, Clear, popover re-sync) refill the inputs without
  // interfering with in-progress typing.
  useEffect(() => {
    setLocalMinStr(toDisplay(range.min));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.min]);
  useEffect(() => {
    setLocalMaxStr(toDisplay(range.max));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.max]);

  const commit = (patch) => {
    const next = { ...range, ...patch };
    if (next.min == null && next.max == null) {
      onChange(null);
    } else {
      onChange({ range: next });
    }
  };

  const handleMinChange = (e) => {
    const accepted = processNumericInput(e.target.value, localMinStr, opts);
    // Skip both setState and commit when the keystroke was rejected (e.g.
    // a non-numeric char, or a `-` for Employees that got stripped to ''
    // matching the previous empty state). React would no-op the setState
    // anyway, but skipping the commit avoids a redundant onChange spam.
    if (accepted === localMinStr) return;
    setLocalMinStr(accepted);

    // Transient `-` is the only accepted string with no committable numeric
    // value yet — keep typing without writing to the filter.
    if (accepted === '-') return;
    const parsed = parseAcceptedNumber(accepted);
    commit({ min: parsed == null ? null : parsed * scale });
  };

  const handleMaxChange = (e) => {
    const accepted = processNumericInput(e.target.value, localMaxStr, opts);
    if (accepted === localMaxStr) return;
    setLocalMaxStr(accepted);

    if (accepted === '-') return;
    const parsed = parseAcceptedNumber(accepted);
    commit({ max: parsed == null ? null : parsed * scale });
  };

  return (
    <div className="filter-numeric-range">
      <div className="filter-help">
        Range in current set:{' '}
        <strong>
          {extentRaw.min == null ? '—' : formatNumericForChip(columnKey, extentRaw.min)}
        </strong>{' '}
        to{' '}
        <strong>
          {extentRaw.max == null ? '—' : formatNumericForChip(columnKey, extentRaw.max)}
        </strong>
      </div>
      <div className="filter-range-inputs">
        <input
          type="text"
          inputMode="decimal"
          placeholder="Min"
          aria-label={`Minimum ${label}`}
          value={localMinStr}
          onChange={handleMinChange}
        />
        <span className="filter-range-sep">to</span>
        <input
          type="text"
          inputMode="decimal"
          placeholder="Max"
          aria-label={`Maximum ${label}`}
          value={localMaxStr}
          onChange={handleMaxChange}
        />
        {unit && <span className="filter-range-unit">{unit}</span>}
      </div>
    </div>
  );
});

FilterNumericRange.propTypes = {
  columnKey: PropTypes.string.isRequired,
  value: PropTypes.shape({
    range: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number
    })
  }),
  onChange: PropTypes.func.isRequired,
  results: PropTypes.array.isRequired
};

export default FilterNumericRange;
